import _ from 'lodash';
import moment from 'moment';
import { isValidElement, useEffect, useMemo, useRef, useState } from 'react';
import {
  TYPE_BEAT_ENUM,
  TYPE_BEAT_EVENT_ENUM,
  TYPE_RHYTHM_EVENT_ENUM,
  canShowCountBeatEvents,
  typesPersistEventV2,
} from '@/shared/config';
import { storeTimeout } from '@/shared/lib/storeTimeout';
import { getEventTypeByGroupAction } from '@/shared/lib/aiUtils';
import { checkAndProcessBeatData, generateUpdatedBeatsV2 } from '@/entities/beat';
import { checkAndProcessEcgData } from '@/entities/ecg';
import {
  getEventEffectdByUpdatedEvent,
  calculateStartStopStripMomentOfListEventV2,
  shouldApplyGroupChange,
} from '@/entities/event';
import { logError } from '@/shared/lib/apiHelpers';
import { getTotalTimeWithin } from '@/shared/lib/timeRange';
import {
  selectBeatChanges,
  selectBulkBeatsChanges,
  selectEventChanges,
  selectEventNews,
  selectGroupEventChanges,
  selectResetBulkBeatsChanges,
  useReportStore,
} from '@/entities/report';
import type { RhythmEventPreviewStripProps } from '.';

// Actions that change the event type itself — only these should override eventType via getEventTypeByGroupAction.
// Review/delete actions must fall back to baseEvent.type so overlay flags (isBeatEvent, isNoHrEvent) remain correct.
const TYPE_CHANGING_GROUP_ACTIONS = new Set([
  'markedAsArtifact',
  'changeAllToVT',
  'changeAllToSVT',
  'changeAllToVE',
  'changeAllToSVE',
]);

/**
 * Prepares the display state for a single rhythm or beat event preview strip, resolving pending edits and loading its waveform. Merges individual and group event changes over the source event to derive the effective type, edited flag and reviewed state, downloads ECG and beat data while the viewer is rendering, narrows the channel list, computes total duration for persisted event types, and clears its cache timers on unmount.
 *
 * @param props - Preview strip props carrying the event, keyRecoil, ecgDataMap, channels, index, activeButton, customOffset, overlayContent and onClickSelectEvent.
 * @returns An object carrying overlayContent, isNoHrEvent, isBeatEvent, effectiveType, isShowCountBeats, baseEvent, onClick, isEdited, groupReviewedState, ecgData, beatMarkers, hesBeatStatus, channels, totalDuration and typeRightSource.
 */
const useRhythmEventPreviewStrip = ({
  keyRecoil,
  activeButton,
  overlayContent = null,
  event,
  channels: channelsProp,
  index,
  ecgDataMap,
  customOffset,
  onClickSelectEvent,
}: Pick<
  RhythmEventPreviewStripProps,
  | 'keyRecoil'
  | 'activeButton'
  | 'overlayContent'
  | 'event'
  | 'channels'
  | 'index'
  | 'ecgDataMap'
  | 'customOffset'
  | 'onClickSelectEvent'
>) => {
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  const groupEventChanges = useReportStore(selectGroupEventChanges(keyRecoil));
  const eventNews = useReportStore(selectEventNews(keyRecoil));
  const beatChanges = useReportStore(selectBeatChanges(keyRecoil));
  const isRenderViewerEcg = useReportStore((s) => s.isRenderViewerEcg);
  const [ecgData, setEcgData] = useState([]);
  const [beatMarkers, setBeatMarkers] = useState([]);
  const [hesBeatStatus, setHesBeatStatus] = useState([]);
  const bulkBeatsChanges = useReportStore(selectBulkBeatsChanges(keyRecoil));
  const resetBulkBeatsChanges = useReportStore(selectResetBulkBeatsChanges(keyRecoil));

  const timeoutRef = useRef<any>();
  let isActiveQuery: boolean;
  const eleChange = useMemo(() => {
    const { event: effectedEvent } = getEventEffectdByUpdatedEvent(event, eventNews, eventChanges);
    return effectedEvent;
  }, [eventChanges, event, eventNews]);
  const baseEvent = eleChange || event;
  const eleChangeGroup = useMemo(
    () => groupEventChanges.find((x) => shouldApplyGroupChange(baseEvent, x)),
    [baseEvent, groupEventChanges],
  );

  /**
   * Warms the ECG cache around a preview strip's event after a delay so neighbouring strips render without a refetch. Schedules a timeout that computes the default start and stop strip moments from the event startTimeEpoch, derives the sample count from the sampling frequency, calls checkAndProcessEcgData, and registers then removes the timer id in storeTimeout under the active button.
   *
   * @param waitTime - Delay in milliseconds before the cache download runs; defaults to 0.
   * @returns Nothing.
   */
  const downloadCacheECGDataV2 = (waitTime = 0) => {
    const timerId = setTimeout(() => {
      storeTimeout.removeStore(activeButton, timerId);
      const { defaultStartStrip, defaultStopStrip } = calculateStartStopStripMomentOfListEventV2({
        startTimeEpoch: event.startTimeEpoch,
      });
      checkAndProcessEcgData({
        ecgDataMap,
        dataLength:
          ((defaultStopStrip.valueOf() - defaultStartStrip.valueOf()) / 1000) *
          ecgDataMap.samplingFrequency,
        // checkAndProcessEcgData's momentObject type (common/handlerEcgData.ts, Task 7) declares
        // string | number, but the function itself does moment(momentObject.startMoment) - it accepts Moment too.
        momentObject: {
          startMoment: defaultStartStrip,
          stopMoment: defaultStopStrip,
        } as any,
      });
    }, waitTime);
    timeoutRef.current = timerId;
    storeTimeout.pushStore(activeButton, timerId);
  };

  /**
   * Loads the ECG waveform and beat markers for one event preview strip and pushes them into component state. Awaits checkAndProcessEcgData and checkAndProcessBeatData over a five-second window around the event, and while the query is still active applies beat and bulk beat changes through generateUpdatedBeatsV2, stores ecgData, beatMarkers and hesBeatStatus, then schedules a staggered cache warm-up; canceled errors are ignored and the rest logged.
   *
   * @returns A promise resolving once the strip's ECG data and beat markers are stored and the cache warm-up is scheduled.
   */
  const downloadEventStrip = async () => {
    try {
      const dataLength = ecgDataMap.samplingFrequency * 5;
      let ecgData: any = await checkAndProcessEcgData({
        ecgDataMap,
        beat: event,
        dataLength,
        config: { customOffset },
      });
      // Check and process beat data
      let beatData: any = await checkAndProcessBeatData({
        ecgDataMap,
        beat: event,
        config: { customOffset },
      });

      if (isActiveQuery) {
        setEcgData(ecgData);
        if (beatData) {
          // generateUpdatedBeatsV2 (BeatHR/helper, Task 8) also declares a required beatTemplateIds field this call site doesn't pass - pre-existing.
          const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeatsV2({
            beats: beatData?.beats,
            hesBeatStatus: beatData?.hesBeatStatus,
            beatChanges,
            startEpoch: moment(event.id - customOffset[0]).valueOf(),
            stopEpoch: moment(event.id + customOffset[1]).valueOf(),
            beatHeatMapIds: beatData?.beatHeatMapIds,
            applyBulkBeatsChanges: bulkBeatsChanges,
            resetAppliedBulkBeatsChanges: resetBulkBeatsChanges,
          } as any);
          setBeatMarkers(updatedBeats);
          setHesBeatStatus(updatedHesBeatStatus);
        }

        downloadCacheECGDataV2(index * 300);
      }

      ecgData = null;
      beatData = null;
    } catch (error) {
      if (!_.includes(error.message, 'canceled')) {
        logError(error.message, 'Error');
      }
    }
  };

  /**
   * Handles a click on a rhythm event preview strip and forwards the selection to the owning list. Stops propagation and default when an overlay element is rendered or the shift, ctrl or meta key is held, otherwise calls onClickSelectEvent with the event and its index.
   *
   * @param e - Mouse click event, inspected for shiftKey, ctrlKey and metaKey and used to cancel propagation.
   * @returns Nothing.
   */
  const onClick = (e: any) => {
    // Prevent click to select new event,shift or ctrl when select multiple
    if (isValidElement(overlayContent) || e?.shiftKey || e?.ctrlKey || e?.metaKey) {
      e?.stopPropagation();
      e?.preventDefault();
      return;
    }
    onClickSelectEvent(event, index);
  };

  useEffect(() => {
    isActiveQuery = true;
    if (isRenderViewerEcg) {
      downloadEventStrip();
    }
    return () => {
      isActiveQuery = false;
    };
  }, [event, beatChanges, isRenderViewerEcg, bulkBeatsChanges, resetBulkBeatsChanges]);

  useEffect(
    () => () => {
      storeTimeout.removeStore(activeButton, timeoutRef.current);
    },
    [activeButton],
  );

  const isEdited = !!eleChange || !!eleChangeGroup;
  const eventType = eleChangeGroup
    ? TYPE_CHANGING_GROUP_ACTIONS.has(eleChangeGroup.actionType)
      ? getEventTypeByGroupAction(eleChangeGroup.actionType)
      : baseEvent?.type
    : baseEvent?.type;
  const typeRightSource = eleChangeGroup
    ? getEventTypeByGroupAction(eleChangeGroup.actionType) || baseEvent?.type
    : baseEvent?.type;
  const groupReviewedState =
    eleChangeGroup?.actionType === 'markedAsReviewed'
      ? true
      : eleChangeGroup?.actionType === 'markedAsUnreviewed'
        ? false
        : undefined;
  const channels = channelsProp?.includes(4) ? [baseEvent?.channel || event.channel] : channelsProp;
  const effectiveType =
    eventType === TYPE_BEAT_ENUM.DELETED ? baseEvent?.oldType || eventType : eventType;
  const isBeatEvent =
    !!(TYPE_BEAT_EVENT_ENUM as any)[effectiveType] &&
    effectiveType !== TYPE_BEAT_EVENT_ENUM.VE_RUN &&
    effectiveType !== TYPE_BEAT_EVENT_ENUM.SVE_RUN;
  const isNoHrEvent =
    isBeatEvent ||
    effectiveType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT ||
    effectiveType === TYPE_RHYTHM_EVENT_ENUM.PAUSE;
  const isShowCountBeats = canShowCountBeatEvents.includes(activeButton);

  const totalDuration = useMemo(() => {
    const eventInfo = baseEvent;
    if (
      typesPersistEventV2.includes(eventInfo?.type) ||
      typesPersistEventV2.includes(eventInfo?.oldType)
    ) {
      return getTotalTimeWithin(ecgDataMap, eventInfo?.start, eventInfo?.stop);
    }
    return eventInfo.duration;
  }, [baseEvent, ecgDataMap]);

  return {
    overlayContent,
    isNoHrEvent,
    isBeatEvent,
    effectiveType,
    isShowCountBeats,
    baseEvent,
    onClick,
    isEdited,
    groupReviewedState,
    ecgData,
    beatMarkers,
    hesBeatStatus,
    channels,
    totalDuration,
    typeRightSource,
  };
};

export default useRhythmEventPreviewStrip;
